import { useTranslation } from "react-i18next";
import { Button, OutlineButton } from "components/button";
import { CampaignType } from "../types/ICampaign";

interface SendConfirmModalProps {
  isOpen: boolean;
  campaignName: string;
  subject: string;
  recipientCount: number | null;
  isSending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  campaignType: CampaignType;
  messageText?: string | null;
}

const SendConfirmModal = ({
  isOpen,
  campaignName,
  subject,
  recipientCount,
  isSending,
  onConfirm,
  onCancel,
  campaignType,
  messageText,
}: SendConfirmModalProps) => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.sendConfirm";
  const tr = (key: string) => t(`${tBase}.${key}`);

  if (!isOpen) {
    return null;
  }

  const isLargeSend = (recipientCount ?? 0) > 1000;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4">
          {tr("title")}
        </h2>

        <div className="space-y-3 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{tr("campaignName")}:</span>
            <span className="font-medium">{campaignName}</span>
          </div>
          {campaignType === "sms" ? (
            <div>
              <span className="text-gray-500 text-sm">{tr("messagePreview")}:</span>
              <div className="mt-1 p-3 bg-gray-100 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                {messageText || "--"}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {tr("smsStopNote")}
              </p>
            </div>
          ) : (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{tr("subject")}:</span>
              <span className="font-medium">{subject}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{tr("totalRecipients")}:</span>
            <span className="font-bold text-[#ff4f00]">
              {recipientCount ?? "--"}
            </span>
          </div>
        </div>

        {isLargeSend && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-6">
            <p className="text-sm text-yellow-700 font-medium">
              {tr("largeSendWarning")}
            </p>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-6">{tr("confirmMessage")}</p>

        <div className="flex justify-end gap-3">
          <OutlineButton
            size="small"
            onClick={onCancel}
            disabled={isSending}
          >
            {tr("cancel")}
          </OutlineButton>
          <Button
            primary
            size="small"
            onClick={onConfirm}
            disabled={isSending}
            isLoading={isSending}
          >
            {isSending ? tr("sending") : tr("sendNow")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendConfirmModal;
